<template>
  <div>
    <!-- Loading -->
    <div v-if="loading" class="text-center py-16 text-gray-500">
      <i class="pi pi-spinner pi-spin text-3xl"></i>
      <p class="mt-2">Loading all semester results...</p>
    </div>

    <!-- Error -->
    <div v-else-if="error" class="text-center py-16">
      <div class="w-16 h-16 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-4">
        <i class="pi pi-exclamation-triangle text-2xl text-red-400"></i>
      </div>
      <h3 class="text-lg font-bold text-gray-800 mb-2">Could not load results</h3>
      <p class="text-sm text-gray-500">{{ error.message || 'An error occurred while loading your results.' }}</p>
    </div>

    <!-- Empty -->
    <div v-else-if="!semesters.length" class="text-center py-16">
      <div class="w-16 h-16 rounded-full bg-amber-50 flex items-center justify-center mx-auto mb-4">
        <i class="pi pi-file-excel text-2xl text-amber-400"></i>
      </div>
      <h3 class="text-lg font-bold text-gray-800 mb-2">No Published Results</h3>
      <p class="text-sm text-gray-500">No senate-approved results have been published yet.</p>
    </div>

    <!-- Semester Cards -->
    <div v-else class="space-y-8">
      <div v-for="(semester, idx) in semesters" :key="idx"
        class="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">

        <!-- Semester Header -->
        <div class="bg-gradient-to-r from-primary/80 to-emerald-600 text-white px-6 py-4 flex items-center justify-between">
          <div>
            <h3 class="text-lg font-bold">
              {{ semester.academic_calendar?.session?.name }} &mdash; {{ semester.academic_calendar?.termStructure?.name }}
            </h3>
            <p v-if="semester.programme" class="text-sm text-white/80 mt-1">
              {{ semester.programme.program_name }} ({{ semester.programme.degree }})
            </p>
          </div>
          <div class="text-right">
            <div class="text-xs text-white/70 uppercase tracking-wider">Cumulative CGPA</div>
            <div class="text-2xl font-bold">{{ formatNumber(semester.cumulative_cgpa) }}</div>
          </div>
        </div>

        <!-- Course Table -->
        <div class="p-6">
          <div class="overflow-x-auto rounded-lg border border-gray-200">
            <table class="w-full text-sm">
              <thead class="bg-gray-50 text-gray-500 font-semibold uppercase tracking-wider text-xs border-b border-gray-200">
                <tr>
                  <th v-for="header in getVisibleHeaders(semester)" :key="header.key" class="px-4 py-3 text-left whitespace-nowrap">
                    {{ header.label }}
                  </th>
                </tr>
              </thead>
              <tbody class="divide-y divide-gray-100">
                <tr v-for="(row, rIdx) in getSemesterData(semester)" :key="rIdx" class="hover:bg-gray-50/50">
                  <td v-for="header in getVisibleHeaders(semester)" :key="header.key"
                    class="px-4 py-3 whitespace-nowrap text-gray-700"
                    :class="{ 'font-medium text-gray-900': header.key === 'subject' || header.key === 'course' }">
                    {{ row[header.key] ?? '-' }}
                  </td>
                </tr>
                <tr v-if="!getSemesterData(semester).length">
                  <td :colspan="getVisibleHeaders(semester).length" class="px-4 py-8 text-center text-gray-400">
                    No result data for this semester.
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- GPA Row -->
          <div class="flex items-center justify-between mt-4 px-2">
            <div class="flex items-center gap-6 text-sm">
              <span class="text-gray-500">Semester GPA: <strong class="text-gray-900">{{ formatNumber(semester.gpa) }}</strong></span>
              <span class="text-gray-500">CGPA: <strong class="text-emerald-700">{{ formatNumber(semester.cumulative_cgpa) }}</strong></span>
            </div>
            <Tag :value="getClassification(semester.cumulative_cgpa)" :severity="getClassificationSeverity(semester.cumulative_cgpa)" />
          </div>
        </div>
      </div>

      <!-- Download All Button -->
      <div class="flex justify-center pt-4">
        <Button
          label="Download All Semesters PDF"
          icon="pi pi-download"
          :loading="downloading"
          @click="downloadAllPdf"
          severity="success"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useApolloClient, useQuery } from '@vue/apollo-composable';
import { useToast } from 'primevue/usetoast';
import {
  GET_STUDENT_ALL_SEMESTER_RESULTS,
  DOWNLOAD_STUDENT_ALL_SEMESTERS_RESULT,
} from '@/graphql/Queries/index';

interface SemesterHeader {
  key: string;
  label: string;
  type?: string | null;
  is_visible?: boolean | null;
}

type SemesterDataRow = Record<string, unknown>;

interface SemesterResultRow {
  academic_calendar?: {
    session?: { name?: string | null } | null;
    termStructure?: { name?: string | null } | null;
  } | null;
  programme?: { program_name?: string | null; degree?: string | null } | null;
  headers?: SemesterHeader[] | null;
  data?: SemesterDataRow[] | { subjects?: SemesterDataRow[] } | null;
  gpa?: number | null;
  cumulative_cgpa?: number | null;
}

const props = defineProps<{ studentId: string; programmeId?: string }>();
const toast = useToast();

const { result, loading, error } = useQuery(GET_STUDENT_ALL_SEMESTER_RESULTS, () => ({
  student_id: props.studentId,
  programme_id: props.programmeId || undefined,
}), { fetchPolicy: 'network-only' });

const semesters = computed(() => result.value?.studentAllSemesterResults?.semesters ?? []);

function getVisibleHeaders(semester: SemesterResultRow): SemesterHeader[] {
  const headers = (semester.headers || []).filter((h) => h.is_visible !== false);
  return getSemesterData(semester).some(row => row.module_name) && !headers.some(h => h.key === 'module_name')
    ? [...headers, { key: 'module_name', label: 'Module', is_visible: true }] : headers;
}

function getSemesterData(semester: SemesterResultRow): SemesterDataRow[] {
  const data = semester.data;
  if (Array.isArray(data)) return data;

  return (data as { subjects?: SemesterDataRow[] } | null)?.subjects ?? [];
}

function formatNumber(val: number | null | undefined): string {
  if (val === null || val === undefined) return 'N/A';
  return Number(val).toFixed(2);
}

function getClassification(cgpa: number | null | undefined): string {
  if (!cgpa) return 'N/A';
  if (cgpa >= 4.50) return 'First Class';
  if (cgpa >= 3.50) return 'Second Class Upper';
  if (cgpa >= 2.40) return 'Second Class Lower';
  if (cgpa >= 1.50) return 'Third Class';
  if (cgpa >= 1.00) return 'Pass';
  return 'Fail';
}

function getClassificationSeverity(cgpa: number | null | undefined): 'success' | 'info' | 'warn' | 'danger' | 'secondary' {
  if (!cgpa) return 'secondary';
  if (cgpa >= 3.50) return 'success';
  if (cgpa >= 2.40) return 'info';
  if (cgpa >= 1.50) return 'warn';
  return 'danger';
}

const { resolveClient } = useApolloClient();
const downloading = ref(false);

async function downloadAllPdf() {
  downloading.value = true;
  try {
    const client = resolveClient();
    const { data } = await client.query({
      query: DOWNLOAD_STUDENT_ALL_SEMESTERS_RESULT,
      variables: { student_id: props.studentId, programme_id: props.programmeId || undefined },
      fetchPolicy: 'no-cache',
    });
    if (data?.downloadStudentAllSemestersResult?.id) {
      toast.add({
        severity: 'success',
        summary: 'Export Started',
        detail: 'Your all-semesters PDF is being generated. Check the Downloads icon for progress.',
        life: 5000,
      });
    } else {
      toast.add({ severity: 'warn', summary: 'Could not start export', detail: 'Please try again.', life: 4000 });
    }
  } catch {
    toast.add({ severity: 'error', summary: 'Download Failed', detail: 'Could not generate the PDF.', life: 4000 });
  } finally {
    downloading.value = false;
  }
}
</script>
